import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { catalogService, CatalogItem } from '../../services/catalog.service';
import { manualEntryService, ManualEntry } from '../../services/manual-entry.service';
import { projectService, Project } from '../../services/project.service';
import { clientService, Client } from '../../services/client.service';

type TabType = 'income' | 'expense';

interface FormState {
  folio: string;
  entry_date: string;
  document_number: string;
  beneficiary_type: '' | 'office' | 'supplier' | 'client' | 'contractor';
  beneficiary_id: string;
  beneficiary_external_name: string;
  concept_id: string;
  project_id: string;
  details: string;
  movement_type_id: string;
  movement_detail_id: string;
  amount: number;
  affects_wallet: boolean;
  department_id: string;
  currency_type_id: string;
}

const EMPTY_FORM: FormState = {
  folio: '',
  entry_date: new Date().toISOString().slice(0, 10),
  document_number: '',
  beneficiary_type: '',
  beneficiary_id: '',
  beneficiary_external_name: '',
  concept_id: '',
  project_id: '',
  details: '',
  movement_type_id: '',
  movement_detail_id: '',
  amount: 0,
  affects_wallet: true,
  department_id: '',
  currency_type_id: 'ct-cash',
};

export function ManualCapturePage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('income');
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Catálogos
  const [concepts, setConcepts] = useState<CatalogItem[]>([]);
  const [movementTypes, setMovementTypes] = useState<CatalogItem[]>([]);
  const [movementDetails, setMovementDetails] = useState<CatalogItem[]>([]);
  const [departments, setDepartments] = useState<CatalogItem[]>([]);
  const [currencyTypes, setCurrencyTypes] = useState<CatalogItem[]>([]);
  const [suppliers, setSuppliers] = useState<CatalogItem[]>([]);
  const [contractors, setContractors] = useState<CatalogItem[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);

  // Listado
  const [entries, setEntries] = useState<ManualEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadCatalogs();
    loadProjects();
    loadClients();
  }, []);

  useEffect(() => {
    loadFilteredCatalogs();
    loadEntries();
    setForm(prev => ({
      ...prev,
      concept_id: '',
      movement_type_id: '',
      movement_detail_id: '',
    }));
  }, [activeTab]);

  const loadCatalogs = async () => {
    try {
      const [deps, curr, sups, conts] = await Promise.all([
        catalogService.list('departments'),
        catalogService.list('currency_types'),
        catalogService.list('suppliers'),
        catalogService.list('contractors'),
      ]);
      setDepartments(deps);
      setCurrencyTypes(curr);
      setSuppliers(sups);
      setContractors(conts);
    } catch (err: any) {
      setError(err.message || 'Error al cargar catálogos');
    }
  };

  const loadFilteredCatalogs = async () => {
    try {
      const kind = activeTab === 'income' ? 'I' : 'E';
      const [conc, mTypes, mDetails] = await Promise.all([
        catalogService.list('concepts', { kind }),
        catalogService.list('movement_types', { kind }),
        catalogService.list('movement_details', { kind }),
      ]);
      setConcepts(conc);
      setMovementTypes(mTypes);
      setMovementDetails(mDetails);
    } catch (err: any) {
      setError(err.message || 'Error al cargar catálogos filtrados');
    }
  };

  const loadProjects = async () => {
    try {
      const data = await projectService.getProjects();
      const active = data.filter((p: any) =>
        ['planning', 'in_progress', 'on_hold'].includes(p.status)
      );
      setProjects(active);
    } catch (err: any) {
      console.error('Error al cargar proyectos:', err);
    }
  };

  const loadClients = async () => {
    try {
      const data = await clientService.getClients();
      setClients(data.filter((c: any) => c.status === 'active'));
    } catch (err: any) {
      console.error('Error al cargar clientes:', err);
    }
  };

  const loadEntries = async () => {
    setIsLoading(true);
    try {
      const data = await manualEntryService.list({ entry_type: activeTab });
      setEntries(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar registros');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof FormState, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    setSuccess('');

    if (!form.concept_id) {
      setError('Selecciona un concepto');
      setIsSaving(false);
      return;
    }
    if (!form.amount || form.amount <= 0) {
      setError('El importe debe ser mayor a 0');
      setIsSaving(false);
      return;
    }
    if (form.affects_wallet && !form.department_id) {
      setError('Si afecta wallet, debes seleccionar un departamento');
      setIsSaving(false);
      return;
    }
    // Validar beneficiario si se seleccionó un tipo
    if (form.beneficiary_type && form.beneficiary_id === '__OTHER__' && !form.beneficiary_external_name) {
      setError('Ingresa el nombre del beneficiario');
      setIsSaving(false);
      return;
    }

    try {
      const isExternal = form.beneficiary_id === '__OTHER__';

      const payload: any = {
        entry_type: activeTab,
        folio: form.folio || undefined,
        entry_date: new Date(form.entry_date).getTime(),
        document_number: form.document_number || undefined,
        beneficiary_type: form.beneficiary_type || undefined,
        beneficiary_id: isExternal ? undefined : (form.beneficiary_id || undefined),
        beneficiary_external_name: isExternal ? form.beneficiary_external_name : undefined,
        concept_id: form.concept_id,
        project_id: form.project_id || undefined,
        details: form.details || undefined,
        movement_type_id: form.movement_type_id || undefined,
        movement_detail_id: form.movement_detail_id || undefined,
        amount: form.amount,
        affects_wallet: form.affects_wallet ? 1 : 0,
        department_id: form.department_id || undefined,
        currency_type_id: form.affects_wallet ? form.currency_type_id : undefined,
      };

      await manualEntryService.create(payload);
      setSuccess('Registro guardado correctamente');
      setForm({ ...EMPTY_FORM, entry_date: new Date().toISOString().slice(0, 10) });
      await loadEntries();
    } catch (err: any) {
      setError(err.message || 'Error al guardar registro');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReverse = async (id: string) => {
    if (!confirm('¿Revertir este registro? Se creará un movimiento inverso.')) return;
    try {
      await manualEntryService.reverse(id);
      await loadEntries();
    } catch (err: any) {
      setError(err.message || 'Error al revertir');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('es-MX');
  };

  // Opciones dinámicas de beneficiario
  const getBeneficiaryOptions = (): { value: string; label: string }[] => {
    let base: { value: string; label: string }[] = [];

    switch (form.beneficiary_type) {
      case 'client':
        base = clients.map(c => ({ value: c.id, label: c.name }));
        break;
      case 'supplier':
        base = suppliers.map(s => ({ value: s.id, label: s.name }));
        break;
      case 'contractor':
        base = contractors.map(c => ({ value: c.id, label: c.name }));
        break;
      case 'office':
        base = departments.map(d => ({ value: d.id, label: d.name }));
        break;
      default:
        base = [];
    }

    // Añadir opción "+ Otro" para tipos que lo permitan
    if (form.beneficiary_type === 'supplier' || form.beneficiary_type === 'contractor') {
      base.push({ value: '__OTHER__', label: '+ Otro (escribir nombre)' });
    }

    return base;
  };

  const beneficiaryOptions = getBeneficiaryOptions();
  const showExternalInput = form.beneficiary_id === '__OTHER__';

  return (
    <div className="page-container">
      <div className="page-header-bar">
        <div>
          <h1>Captura Manual</h1>
          <p className="subtitle">Registro manual de ingresos y egresos</p>
        </div>
      </div>

      <div className="tabs-bar">
        <button
          onClick={() => setActiveTab('income')}
          className={`tab-button ${activeTab === 'income' ? 'active' : ''}`}
        >
          📥 Ingresos
        </button>
        <button
          onClick={() => setActiveTab('expense')}
          className={`tab-button ${activeTab === 'expense' ? 'active' : ''}`}
        >
          📤 Egresos
        </button>
      </div>

      {error && (
        <div className="info-panel error">
          <span className="info-panel-icon">❌</span>
          <div className="info-panel-body">
            <p className="info-panel-text">{error}</p>
          </div>
        </div>
      )}
      {success && (
        <div className="info-panel success">
          <span className="info-panel-icon">✅</span>
          <div className="info-panel-body">
            <p className="info-panel-text">{success}</p>
          </div>
        </div>
      )}

      {/* Formulario */}
      <div className="card">
        <h3>{activeTab === 'income' ? 'Nuevo Ingreso' : 'Nuevo Egreso'}</h3>
        <form onSubmit={handleSubmit} style={{ marginTop: '16px' }}>
          <div className="form-row cols-3">
            <div className="form-col">
              <label className="form-label">Folio</label>
              <input
                type="text"
                className="form-input"
                value={form.folio}
                onChange={(e) => handleChange('folio', e.target.value)}
                placeholder="Ej: ING-001"
              />
            </div>
            <div className="form-col">
              <label className="form-label required">Fecha</label>
              <input
                type="date"
                className="form-input"
                required
                value={form.entry_date}
                onChange={(e) => handleChange('entry_date', e.target.value)}
              />
            </div>
            <div className="form-col">
              <label className="form-label">No. Documento</label>
              <input
                type="text"
                className="form-input"
                value={form.document_number}
                onChange={(e) => handleChange('document_number', e.target.value)}
                placeholder="Número de documento"
              />
            </div>
          </div>

          {/* Beneficiario */}
          <div className="form-row">
            <div className="form-col">
              <label className="form-label">Tipo de Beneficiario</label>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '4px' }}>
                {[
                  { value: 'office', label: '🏢 Oficina' },
                  { value: 'supplier', label: '🏭 Proveedor' },
                  { value: 'client', label: '👤 Cliente' },
                  { value: 'contractor', label: '🔨 Contratista' },
                ].map(opt => (
                  <label key={opt.value} className="radio-field">
                    <input
                      type="radio"
                      name="beneficiary_type"
                      value={opt.value}
                      checked={form.beneficiary_type === opt.value}
                      onChange={(e) => {
                        handleChange('beneficiary_type', e.target.value);
                        handleChange('beneficiary_id', '');
                        handleChange('beneficiary_external_name', '');
                      }}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {form.beneficiary_type && (
            <div className="form-row">
              <div className="form-col">
                <label className="form-label">Beneficiario</label>
                <select
                  className="form-select"
                  value={form.beneficiary_id}
                  onChange={(e) => {
                    handleChange('beneficiary_id', e.target.value);
                    if (e.target.value !== '__OTHER__') {
                      handleChange('beneficiary_external_name', '');
                    }
                  }}
                >
                  <option value="">Seleccionar...</option>
                  {beneficiaryOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>

                {showExternalInput && (
                  <input
                    type="text"
                    className="form-input"
                    style={{ marginTop: '8px' }}
                    value={form.beneficiary_external_name}
                    onChange={(e) => handleChange('beneficiary_external_name', e.target.value)}
                    placeholder="Nombre del beneficiario"
                  />
                )}

                {form.beneficiary_type === 'supplier' && suppliers.length === 0 && !showExternalInput && (
                  <span className="form-help" style={{ marginTop: '4px' }}>
                    No hay proveedores registrados. Puedes capturarlo como "+ Otro" o darlo de alta en Catálogos.
                  </span>
                )}
                {form.beneficiary_type === 'contractor' && contractors.length === 0 && !showExternalInput && (
                  <span className="form-help" style={{ marginTop: '4px' }}>
                    No hay contratistas registrados. Puedes capturarlo como "+ Otro" o darlo de alta en Catálogos.
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="form-row cols-2">
            <div className="form-col">
              <label className="form-label required">Concepto</label>
              <select
                className="form-select"
                required
                value={form.concept_id}
                onChange={(e) => handleChange('concept_id', e.target.value)}
              >
                <option value="">Seleccionar concepto...</option>
                {concepts.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="form-col">
              <label className="form-label">Contrato / Proyecto</label>
              <select
                className="form-select"
                value={form.project_id}
                onChange={(e) => handleChange('project_id', e.target.value)}
              >
                <option value="">Sin proyecto</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-col">
              <label className="form-label">Detalles</label>
              <textarea
                className="form-textarea"
                rows={2}
                value={form.details}
                onChange={(e) => handleChange('details', e.target.value)}
                placeholder="Observaciones o detalles del movimiento..."
              />
            </div>
          </div>

          <div className="form-row cols-2">
            <div className="form-col">
              <label className="form-label">Tipo de Movimiento</label>
              <select
                className="form-select"
                value={form.movement_type_id}
                onChange={(e) => handleChange('movement_type_id', e.target.value)}
              >
                <option value="">Seleccionar tipo...</option>
                {movementTypes.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <div className="form-col">
              <label className="form-label">Movimiento</label>
              <select
                className="form-select"
                value={form.movement_detail_id}
                onChange={(e) => handleChange('movement_detail_id', e.target.value)}
              >
                <option value="">Seleccionar movimiento...</option>
                {movementDetails.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row cols-2">
            <div className="form-col">
              <label className="form-label required">Importe (MXN)</label>
              <input
                type="number"
                className="form-input"
                required
                min="0"
                step="0.01"
                value={form.amount || ''}
                onChange={(e) => handleChange('amount', parseFloat(e.target.value) || 0)}
                placeholder="0.00"
              />
            </div>
            <div className="form-col">
              <label className="form-label">Departamento</label>
              <select
                className="form-select"
                value={form.department_id}
                onChange={(e) => handleChange('department_id', e.target.value)}
                disabled={!form.affects_wallet}
              >
                <option value="">Seleccionar...</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-col">
              <label className="checkbox-field" style={{ padding: '8px 0' }}>
                <input
                  type="checkbox"
                  checked={form.affects_wallet}
                  onChange={(e) => handleChange('affects_wallet', e.target.checked)}
                />
                <span>
                  <strong>Este movimiento afecta los saldos del wallet</strong>
                  <br />
                  <small style={{ color: 'var(--gray)' }}>
                    Desactiva esta opción si es un registro excepcional sin ingreso/egreso físico real
                  </small>
                </span>
              </label>
            </div>
          </div>

          {form.affects_wallet && (
            <div className="form-row">
              <div className="form-col">
                <label className="form-label">Tipo de moneda / documento</label>
                <select
                  className="form-select"
                  value={form.currency_type_id}
                  onChange={(e) => handleChange('currency_type_id', e.target.value)}
                >
                  {currencyTypes.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button type="submit" disabled={isSaving} className="btn btn-primary">
              {isSaving ? 'Guardando...' : 'Guardar Registro'}
            </button>
            <button
              type="button"
              onClick={() => {
                setForm({ ...EMPTY_FORM, entry_date: new Date().toISOString().slice(0, 10) });
                setError('');
                setSuccess('');
              }}
              className="btn btn-ghost"
            >
              Limpiar
            </button>
          </div>
        </form>
      </div>

      {/* Listado */}
      <div className="card">
        <h3>
          {activeTab === 'income' ? 'Ingresos Registrados' : 'Egresos Registrados'}
          <span style={{ marginLeft: '10px', fontSize: '13px', color: 'var(--gray)', fontWeight: 400 }}>
            ({entries.length})
          </span>
        </h3>

        {isLoading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p className="loading-text">Cargando registros...</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <h3 className="empty-state-title">No hay registros</h3>
            <p className="empty-state-text">
              Aún no se han capturado {activeTab === 'income' ? 'ingresos' : 'egresos'}
            </p>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Folio</th>
                  <th>Concepto</th>
                  <th>Beneficiario</th>
                  <th>Proyecto</th>
                  <th style={{ textAlign: 'right' }}>Importe</th>
                  <th>Estado</th>
                  <th className="col-actions">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => (
                  <tr key={entry.id}>
                    <td>{formatDate(entry.entry_date)}</td>
                    <td>{entry.folio || '—'}</td>
                    <td>{entry.concept_name || '—'}</td>
                    <td>
                      {entry.beneficiary_external_name ||
                        entry.beneficiary_id ||
                        '—'}
                    </td>
                    <td>{entry.project_name || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: activeTab === 'income' ? '#059669' : '#dc2626' }}>
                      {formatCurrency(entry.amount)}
                    </td>
                    <td>
                      <span className={`badge ${entry.status === 'active' ? 'badge-success' : 'badge-neutral'}`}>
                        {entry.status === 'active' ? '✅ Activo' : '🔄 Revertido'}
                      </span>
                    </td>
                    <td className="col-actions">
                      {entry.status === 'active' && (
                        <button
                          onClick={() => handleReverse(entry.id)}
                          className="btn-action-reject"
                          title="Revertir este movimiento"
                        >
                          🔄 Revertir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}